// Pieces of the module pages: a module's coloured badge, its card on Home, the breadcrumb trail and the frame that puts
// a module's illustrative-projects pages under it.
import { Anchor, Badge, Card, Group, Stack, Text } from "@mantine/core";
import type { ReactNode } from "react";
import { Link } from "react-router";
import { examplesPath, moduleBySlug, moduleOfProduct, modulePath, subProductOf, type ModuleInfo, type ModuleSlug } from "../lib/modules";

export function Tag({ children, coming }: { children: ReactNode; coming?: boolean }) {
  return (
    <Text component="span" size="xs" px={7} py={1} mr={4} mb={4} display="inline-block"
      style={{ borderRadius: 4, background: coming ? "transparent" : "var(--mantine-color-default-hover)",
        border: coming ? "1px dashed var(--mantine-color-gray-5)" : "1px solid transparent" }}
      c={coming ? "dimmed" : undefined}>
      {children}
    </Text>
  );
}

/** A project's module as a badge in the module's colour. */
export function ModuleBadge({ product, size = "sm" }: { product: string; size?: "sm" | "md" | "lg" }) {
  const m = moduleOfProduct(product);
  if (!m) return <Badge variant="light" size={size}>{product}</Badge>;
  return (
    <Badge variant="light" size={size} style={{ background: m.tint, color: m.color, textTransform: "none", flexShrink: 0, overflow: "visible" }}
      component={Link} to={modulePath(m.slug)} styles={{ root: { cursor: "pointer" }, label: { overflow: "visible" } }}>
      {m.name}{subProductOf(product) ? ` · ${subProductOf(product)!.short}` : ""}
    </Badge>
  );
}

/** Home's card of a module: the whole card opens the module's home page. */
export function ModuleCard({ m, projects, examples, latest, compact }: {
  m: ModuleInfo; projects?: number; examples?: number; latest?: { name: string; id: string; what: string };
  compact?: boolean;
}) {
  const meta: string[] = [];
  if (m.product) meta.push(`${projects ?? 0} project${projects === 1 ? "" : "s"}`);
  if (m.examplesProduct) meta.push(`${examples ?? 0} illustrative`);
  else if (m.product) meta.push("illustrative coming");
  if (m.slug === "qtetris") meta.push("Level 1 · 2 · 3");
  const tags = compact ? m.tags.slice(0, 4) : m.tags;
  return (
    <Card withBorder component={Link} to={modulePath(m.slug)} data-testid={`module-card-${m.slug}`} p="md"
      style={{ borderTop: `4px solid ${m.color}`, textDecoration: "none", color: "inherit", display: "flex",
        flexDirection: "column", gap: 6 }}>
      <Group justify="space-between" wrap="nowrap" align="flex-start">
        <div>
          <Text fw={700} size="lg" style={{ color: m.color }}>{m.name}</Text>
          <Text size="xs" c="dimmed">{m.subtitle}</Text>
        </div>
        <Text size="xl" style={{ color: m.color }} aria-hidden>→</Text>
      </Group>
      <Text size="sm">{m.question}</Text>
      <div>
        {tags.map((t) => <Tag key={t}>{t}</Tag>)}
        {(m.coming ?? []).map((t) => <Tag key={t} coming>{t} · coming</Tag>)}
      </div>
      {latest && !compact && (
        <Text size="xs">Latest: <Anchor component="span" size="xs">{latest.name}</Anchor>{latest.what ? ` · ${latest.what}` : ""}</Text>
      )}
      <Text size="xs" c="dimmed" mt="auto">{meta.join(" · ")}</Text>
    </Card>
  );
}

/** Home › Module › ... (the last item is the current page, not a link). */
export function Crumbs({ items }: { items: { label: string; to?: string }[] }) {
  return (
    <Group gap={6} mb={6} data-testid="crumbs">
      {items.map((it, i) => (
        <Group gap={6} key={i}>
          {i > 0 && <Text size="sm" c="dimmed">›</Text>}
          {it.to ? <Anchor component={Link} to={it.to} size="sm" c="dimmed">{it.label}</Anchor>
            : <Text size="sm" c="dark.3">{it.label}</Text>}
        </Group>
      ))}
    </Group>
  );
}

/** A module's illustrative-projects pages under the module: the trail above the gallery or one project's guide. */
export function ModuleExamplesFrame({ slug, detail, children }: { slug: ModuleSlug; detail?: boolean; children: ReactNode }) {
  const m = moduleBySlug(slug)!;
  const trail = [{ label: "Home", to: "/" }, { label: m.name, to: modulePath(slug) },
    detail ? { label: "Illustrative projects", to: examplesPath(slug) } : { label: "Illustrative projects" }];
  return <Stack gap={0}><Crumbs items={trail} />{children}</Stack>;
}
